import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import Slider from 'react-slick';
import { withRouter } from 'react-router';
import Icon from 'components/Icon';
import VisibilitySensor from 'react-visibility-sensor';
import HomeSlideHeaderComp from 'components/HomeSlideHeader';
import {
  SITE_PLATFORM_MOBILE,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
} from 'helpers/BreakpointHelper';
import {
  modifyBreakpoints as optimizelyModifyBreakpoints,
  userAttributeEvents as optimizelyUserAttributeEvents,
} from 'helpers/OptimizelyHelper';
import { COLLECTION_HERO_IMAGE } from 'helpers/ImageHelper';
import { noop } from 'helpers/GlobalHelper';
import { additionalPosterEnabled, autoplayEnabled } from 'helpers/ConfigHelper';
import {
  isUpcomingContent,
  isLive,
  filterMissingDeepLinkItems,
  nonFoxNowSkus,
} from 'helpers/ContentHelper';
import { Element } from 'react-scroll';
import { Waypoint } from 'react-waypoint';
import { PAGE_COLLECTION } from 'helpers/PageHelper';
import { isVideoLive } from 'app/screens/Live/LiveHelpers';
import { setTealiumSourceCollectionGlobal } from '@foxcorp/delta-web-analytics';
import { featuredVideoProcessor } from '../../Helpers';
import Style from '../../Collection.module.scss';

const ALL_COLLECTIONS_SCROLL_PREFIX = 'ALL_COLLECTIONS_SCROLL_PREFIX_';

const BREAKPOINTS = (() => {
  const breakpoints = {
    [SITE_PLATFORM_WEB_LARGE]: {
      allowInfoToggle: false,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: true,
    },
    [SITE_PLATFORM_WEB]: {
      allowInfoToggle: false,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: true,
    },
    [SITE_PLATFORM_TABLET]: {
      allowInfoToggle: true,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: false,
    },
    [SITE_PLATFORM_MOBILE]: {
      allowInfoToggle: true,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: false,
    },
  };

  return optimizelyModifyBreakpoints(breakpoints, 'home');
})();

function SampleNextArrow(props) {
  const { className = null, onClick = () => {}, customClassName = null } = props;
  return (
    <button
      className={classNames(className, customClassName)}
      onClick={onClick}
      aria-label="show next content"
    >
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-right" ariaHidden />
    </button>
  );
}

SampleNextArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  customClassName: PropTypes.string,
};

function SamplePrevArrow(props) {
  const { className = null, onClick = () => {}, customClassName = null } = props;
  return (
    <button
      className={classNames(className, customClassName)}
      onClick={onClick}
      aria-label="show previous content"
    >
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-left" ariaHidden />
    </button>
  );
}

SamplePrevArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  customClassName: PropTypes.string,
};

class HeroCarousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSlide: 0,
    };
  }

  render() {
    const {
      accessToken,
      breakpoint,
      coordinates,
      forceKeyartForShowcase,
      globalVideoStartedPlayback,
      handleSaveLiveRestart,
      handleSlideChangeTop,
      idx,
      isAuthenticated,
      isFirst,
      load,
      overlay,
      overridePlayButtonLink,
      overridePlayButtonClick,
      panel,
      panels,
      play,
      preauthorizedResources,
      removeGlobalVideoPlayer,
      removeVideoHero,
      renderHeroImage,
      router,
      saveLiveVideo,
      setCoordinates,
      setOverlay,
      setRecommendationIdOnClick,
      setSuggestedChannel,
      setVideoHero,
      streamExit,
      updatePersistGlobalVideoAfterUmount,
      videoIsLive,
    } = this.props;

    const { currentSlide } = this.state;
    const totalCollections = _.get(panels, 'totalItems');
    const {
      allowInfoToggle,
      arrows,
      showLiveButtons,
      smallCollectionHeader,
      useSeriesLogo,
    } = BREAKPOINTS[breakpoint];
    const { showcaseCarouselAutoscrollSeconds } = window.ExternalConfig;
    const { id: panelId, headline, images, items, collectionType, name: collectionName } = panel;
    if (!items) return null;
    const totalItems = _.get(items, 'totalItems');
    const hasSingleSlide = items.member.length === 1;
    const slideClassName = classNames(
      Style.slide,
      Style.first,
      Style.heroPanel,
      additionalPosterEnabled() ? Style.additionalPoster : null,
      currentSlide === idx ? Style.active : null,
      currentSlide === idx && !play ? Style.inactive : null,
    );
    const headerContainerClass = classNames(
      Style.homeSlideHeaderContainer,
      Style.firstHeaderContainer,
    );
    const isCarousel = items.member.length > 1;

    const currentMembers = _.filter(items.member, item => {
      const type = _.get(item, '@type', null);
      if (isLive(type) && !isVideoLive(item) && !isUpcomingContent(item)) return false;
      return true;
    });
    let filteredItems = currentMembers;
    if (panel?.panelType === 'videos') {
      filteredItems = filterMissingDeepLinkItems(currentMembers, nonFoxNowSkus);
    }
    const heroItems = filteredItems.map((item, idx) => {
      const isSeriesPromo = collectionType === 'seriespromo';
      const heroItem = Object.assign(item);
      heroItem.collectionType = collectionType;
      heroItem.isAuthenticated = isAuthenticated;
      heroItem.isSeriesPromo = isSeriesPromo;
      heroItem.preauthorizedResources = preauthorizedResources;
      heroItem.tabIndex = -1;
      const heroVideo = _.get(heroItem, 'videos[0]', {});
      const processedItem = featuredVideoProcessor({
        breakpoint,
        context: COLLECTION_HERO_IMAGE,
        item: heroItem,
      });
      const { description, id, isLive, name } = processedItem;
      const featuredTitle = headline || name;
      const featuredTitleImage = _.get(images, 'header.raw');
      const onSelection = ({ itemIndex }) => {
        _.set(window, 'context.segment.home.sourceName', featuredTitle);
        const selectedItem = itemIndex !== null ? itemIndex + 1 : 0;
        const data = {
          title: featuredTitle,
          collectionType,
          items,
          selectedItem,
        };
        setRecommendationIdOnClick(items, selectedItem);
        setTealiumSourceCollectionGlobal(data);
      };
      const collectionInfo = {
        collectionType,
        headline,
        isPanelRecommended: panel?.recommended,
        panelId,
        totalCollections,
        totalItems,
        uID: heroVideo?.uID,
      };
      const onPrimaryButtonClick = autoplayEnabled()
        ? e => {
            e.preventDefault();
            const url = e.target.href;
            if (globalVideoStartedPlayback) {
              updatePersistGlobalVideoAfterUmount(true, url);
            } else {
              const source = PAGE_COLLECTION;
              removeGlobalVideoPlayer({ source });
              router.push(url);
            }
          }
        : null;
      return (
        <HomeSlideHeaderComp
          accessToken={accessToken}
          allowInfoToggle={allowInfoToggle}
          breakpoint={breakpoint}
          callCollectionImpressionEvent={this.callCollectionImpressionEvent}
          collectionInfo={collectionInfo}
          coordinates={coordinates}
          description={description}
          disableSingleVideoHeroRender={!isCarousel && globalVideoStartedPlayback && !streamExit}
          disableVideoRender={forceKeyartForShowcase}
          foxAwesomeLayout
          hasSingleSlide={hasSingleSlide}
          heroImage={renderHeroImage(0, idx, isFirst)}
          idx={idx}
          isCarousel={isCarousel}
          isFirst
          isHeroCarousel
          isLive={!!isLive}
          item={processedItem}
          key={`${featuredTitle}-${id}`}
          load={load}
          onPrimaryButtonClick={onPrimaryButtonClick}
          onSelection={onSelection}
          onTitleClick={onSelection}
          onWatch={() => onSelection({ itemIndex: null })}
          overlay={overlay}
          overridePlayButtonClick={overridePlayButtonClick}
          overridePlayButtonLink={overridePlayButtonLink}
          play={!overlay}
          removeVideoHero={removeVideoHero}
          router={router}
          saveLiveRestart={handleSaveLiveRestart}
          saveLiveVideo={saveLiveVideo}
          setCoordinates={setCoordinates}
          setOverlay={setOverlay}
          setSuggestedChannel={setSuggestedChannel}
          setVideoHero={setVideoHero}
          showLiveButtons={showLiveButtons}
          smallCollectionHeader={smallCollectionHeader}
          streamExit={streamExit}
          title={featuredTitle}
          titleImage={featuredTitleImage}
          toggleVideoModal={this.toggleVideoModal}
          useSeriesLogo={useSeriesLogo}
          videoIsLive={videoIsLive}
        />
      );
    });

    const autoplaySpeed = showcaseCarouselAutoscrollSeconds * 1000 || 8000;
    const renderCarousel = (
      <Slider
        dots
        arrows={arrows}
        infinite
        autoplay
        useTransform={false}
        slidesToShow={1}
        autoplaySpeed={autoplaySpeed}
        ref={slider => {
          this.slider = slider;
        }}
        appendDots={dots => (
          <div className={Style.dotsContainer}>
            <ul className={Style.dotsList}>{dots}</ul>
          </div>
        )}
        customPaging={() => <div className={Style.dots} />}
        nextArrow={<SampleNextArrow customClassName={Style.rightArrow} />}
        prevArrow={<SamplePrevArrow customClassName={Style.leftArrow} />}
        beforeChange={(current, next) => {
          this.setState({ currentSlide: next });
        }}
      >
        {heroItems}
      </Slider>
    );

    const renderSinglItem = <div className={Style.singleSlide}>{heroItems}</div>;

    return (
      <VisibilitySensor
        onChange={isVisible => {
          if (this.slider) {
            if (isVisible) this.slider.slickPlay();
            else this.slider.slickPause();
          }
        }}
      >
        <div key={`videoPanel-${panelId}-${idx}`} id={panelId} className={slideClassName}>
          <Element className={headerContainerClass} name={`${ALL_COLLECTIONS_SCROLL_PREFIX}${idx}`}>
            <Waypoint
              onEnter={() => {
                handleSlideChangeTop(idx, true);
                optimizelyUserAttributeEvents().collectionItem(collectionName);
              }}
              onLeave={() => handleSlideChangeTop(idx, false)}
              topOffset="250px"
            >
              <div>{isCarousel ? renderCarousel : renderSinglItem}</div>
            </Waypoint>
          </Element>
        </div>
      </VisibilitySensor>
    );
  }
}

HeroCarousel.defaultProps = {
  accessToken: null,
  account: null,
  anonymousAccount: null,
  blackoutConfig: {},
  coordinates: null,
  favoritesManager: {},
  forceKeyartForShowcase: null,
  globalVideoStartedPlayback: null,
  handleSlideChangeTop: noop,
  idx: null,
  initialShowcase: null,
  isAuthenticated: false,
  isFirst: false,
  load: null,
  location: {},
  mvpdData: null,
  overlay: null,
  overridePlayButtonClick: noop,
  overridePlayButtonLink: false,
  paginatedPanels: {},
  panel: {},
  panels: {},
  panelVideo: {},
  params: { network: null },
  play: null,
  preauthorizedResources: [],
  provider: {},
  renderHeroImage: noop,
  route: {},
  seo: null,
  setCoordinates: noop,
  setOverlay: noop,
  setRecommendationIdOnClick: noop,
  streamExit: null,
  updatePersistGlobalVideoAfterUmount: noop,
  visibleCollections: {},
};

HeroCarousel.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({
    accessToken: PropTypes.string,
  }),
  anonymousAccount: PropTypes.shape({}),
  blackoutConfig: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  coordinates: PropTypes.shape({}),
  favoritesManager: PropTypes.shape({}),
  forceKeyartForShowcase: PropTypes.bool,
  globalVideoStartedPlayback: PropTypes.bool,
  handleSaveLiveRestart: PropTypes.func.isRequired,
  handleSlideChangeTop: PropTypes.func,
  idx: PropTypes.number,
  initialShowcase: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isFirst: PropTypes.bool,
  load: PropTypes.bool,
  location: PropTypes.shape({}),
  mvpdData: PropTypes.shape({}),
  overlay: PropTypes.string,
  overridePlayButtonClick: PropTypes.func,
  overridePlayButtonLink: PropTypes.bool,
  paginatedPanels: PropTypes.shape({}),
  panel: PropTypes.shape({}),
  panels: PropTypes.shape({
    itemsPerPage: PropTypes.number,
    member: PropTypes.arrayOf(PropTypes.shape({})),
    totalItems: PropTypes.number,
  }),
  panelVideo: PropTypes.shape({}),
  params: PropTypes.shape({
    network: PropTypes.string,
  }),
  play: PropTypes.bool,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  provider: PropTypes.shape({}),
  removeGlobalVideoPlayer: PropTypes.func.isRequired,
  removeVideoHero: PropTypes.func.isRequired,
  renderHeroImage: PropTypes.func,
  route: PropTypes.shape({}),
  router: PropTypes.shape({}).isRequired,
  saveLiveVideo: PropTypes.func.isRequired,
  seo: PropTypes.shape({}),
  setCoordinates: PropTypes.func,
  setOverlay: PropTypes.func,
  setRecommendationIdOnClick: PropTypes.func,
  setSuggestedChannel: PropTypes.func.isRequired,
  setVideoHero: PropTypes.func.isRequired,
  streamExit: PropTypes.bool,
  updatePersistGlobalVideoAfterUmount: PropTypes.func,
  videoIsLive: PropTypes.bool.isRequired,
  visibleCollections: PropTypes.shape({}),
};

export default withRouter(HeroCarousel);
